import { Button, Heading, ProgressCircle, Text } from '@react-spectrum/s2';
import Add from '@react-spectrum/s2/icons/Add';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import {
	useCallback,
	useEffect,
	useRef,
	useState,
	type ReactNode,
} from 'react';
import { Dialog } from 'react-aria-components';
import { createPortal } from 'react-dom';

import { FilterToolbar } from '@/spaces/campaigns/CampaignLibraryFilters';
import { StrategyDetail } from '@/spaces/campaigns/StrategyDetail';
import type { PendingProposal } from '@/spaces/campaigns/usePendingProposals';
import { cn } from '@/utils/cn';

import type { StrategyListItem } from '../../../../../bridge/types/strategy.ts';

import { FlagList } from './FlagList';
import { identityFor } from './orbIdentity';
import { StrategyCard } from './StrategyCard';
import { StrategyCreateView } from './StrategyCreateView';
import {
	type DockAnimKind,
	DOCK_FRAME_TRANSITION_NAME,
} from './strategyDockAnimation';
import { groupedDockRows } from './strategyDockData';
import {
	applyStrategyFilters,
	STRATEGY_FILTER_DIMENSIONS,
	type StrategyFilter,
} from './strategyDockFilters';
import { StrategyInviteOrb } from './StrategyOrb';

export interface DockRect {
	left: number;
	top: number;
	width: number;
	height: number;
	// The resting dock bar's height. The menu's top edge is the BAR's top edge (one connected
	// surface), so its content is inset by this much to start below the dock.
	topInset: number;
}

// The strategy agent's seed for a fresh strategy: what it learned from the concluded one.
// Stored on the strategy doc by the propose_new_strategy applier and carried to the
// generation flow so the owner sees the recommendation pre-filled.
export interface StrategySeed {
	recommended_focus: string;
	reasoning: string;
	metrics_to_carry: string[];
	channel_suggestion: string;
}

// Which of the menu's three peer views is showing. Exported because the dock seeds it:
// clicking an orb opens detail, the manage glyph opens the list, and the ghost slot opens
// create — all through this ONE channel, so there is never a second prop racing the first.
export type View =
	| { kind: 'list' }
	| { kind: 'detail'; id: string }
	| { kind: 'create'; seed?: StrategySeed }
	| { kind: 'flags'; id: string };

// The dock+menu surface color: the app-shell gray from RootLayout (`bg-[#f8f8f8]`), which is
// also the --dock-fill @property initial value in index.css. The menu declares it and the bar
// on top inherits it, so the two are provably one color.
export const DOCK_SURFACE_FILL = '#f8f8f8';

// The menu surface the dock opens. A body portal positioned to the derived menu rect: a gray,
// elevated sheet (the app-shell gray of the bar, NOT white) so the white glass strategy cards
// read as raised tiles on it. Its top edge IS the dock bar's top edge and the bar is rendered
// on top of it (the `bar` slot) — so the dock and the menu are ONE connected element sharing one
// fill, not a panel detached below a floating bar. Sizes to its content up to the rect's max
// height, then the card list scrolls. A RAC Dialog inside gives role="dialog"/focus management.
// Escape and an outside click close it (a pointerdown listener, since there is no dimming
// scrim — the chat below stays legible).
//
// It holds three peer VIEWS — the strategy list, one strategy's detail, and creating a new one
// — so every step of managing strategies happens on this one surface. Creation is deliberately
// NOT a dialog stacked on top: that read as a separate thing floating over the dock.
export function DockMenu({
	rect,
	items,
	totalCount,
	loading,
	error,
	businessId,
	initialView,
	focusNonce,
	animKind = 'morph',
	bar,
	onAcknowledgeFlag,
	onOpenOwnerAsk,
	onOpenCampaignSetup,
	onOpenFlagThread,
	onGenerate,
	onRegenerate,
	onActivate,
	onReview,
	onPause,
	onRetry,
	onDeleted,
	onViewSetup,
	proposals,
	progressByStrategyId,
	onRespondProposal,
	onDiscussProposal,
	onClose,
}: {
	rect: DockRect;
	// The curated rows to list.
	items: StrategyListItem[];
	// How many strategies exist in total, curated or not. Distinguishes "you have none yet"
	// (the first-run invitation) from "none are showing right now".
	totalCount: number;
	loading: boolean;
	error: string | null;
	businessId: string;
	// The view this open request wants. ONE channel for all three kinds, so a create intent
	// can't be stomped by an effect that only knows about list-vs-detail.
	initialView: View;
	// Bumped by the parent on every dock open request (manage or a specific orb). The bar
	// persists, so a pinned orb can be clicked while the menu is already open; keying the
	// focus effect on the nonce (not the id) means re-clicking the SAME orb after a Back
	// still re-navigates, while a plain re-render never yanks the user out of the list.
	focusNonce: number;
	// Which motion path the open took. 'morph' → the View Transition tweens the shared frame
	// (nothing extra to do here); 'grow' → no VT support, so the frame plays the CSS grow-in;
	// 'instant' → reduced motion, no animation.
	animKind?: DockAnimKind;
	// The resting dock bar, rendered ON TOP of this surface's top edge (centered, overlaying the
	// `topInset` band) so the dock and the menu read as ONE connected element sharing one fill.
	bar?: ReactNode;
	onAcknowledgeFlag: (strategyId: string, flagId: string) => void;
	onOpenOwnerAsk: (actionId: string, message: string) => void;
	onOpenCampaignSetup: (
		processId: string,
		message: string,
		title: string,
	) => void;
	onOpenFlagThread: (strategyId: string, flagId: string) => void;
	onGenerate: () => Promise<void>;
	// Rebuild ONE dead variant in place. Rejects with owner-facing copy, which the failed view
	// shows — so this must not swallow its own failure.
	onRegenerate: (strategyId: string) => Promise<void>;
	onActivate: (id: string) => Promise<void>;
	onReview: (id: string, title?: string) => void;
	onPause: (id: string) => Promise<void>;
	// Re-read the strategy LIST after it failed to load. Not a per-strategy retry: this fires
	// from the list's own error state, where there is no strategy to name.
	onRetry: () => void | Promise<void>;
	onDeleted: () => void;
	onViewSetup: (processId: string) => void;
	// Agent asks waiting on the owner, across every strategy. Each card takes the ones that
	// belong to it — an ask is about one strategy, so it belongs on that strategy, not in a
	// separate list the owner has to cross-reference.
	//
	// Optional throughout, and absent when empty: a caller that only wants the strategy list
	// should not have to know proposals exist.
	proposals?: PendingProposal[];
	// Measured north-star values keyed by strategy id, so the card gauges can show where the
	// owner actually is. One fetch for the whole list — never one per card.
	progressByStrategyId?: Record<string, { current_value: number }>;
	onRespondProposal?: (
		id: string,
		decision: 'approve' | 'reject',
	) => Promise<void>;
	onDiscussProposal?: (p: PendingProposal, strategyTitle?: string) => void;
	onClose: () => void;
}) {
	const [view, setView] = useState<View>(initialView);
	const frameRef = useRef<HTMLDivElement>(null);

	// On each new open request (nonce change): an orb focuses its detail; the manage
	// affordance (no id) lands on the list. Skips the initial mount (view is already seeded).
	const seenNonceRef = useRef(focusNonce);
	useEffect(() => {
		if (focusNonce === seenNonceRef.current) return;
		seenNonceRef.current = focusNonce;
		setView(initialView);
	}, [focusNonce, initialView]);

	// Escape closes (also handled by RAC Dialog, but this covers the surface reliably).
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [onClose]);

	// Wrap the caller's respond handler: when the owner approves a propose_new_strategy
	// recommendation, navigate to the create view with the agent's seed pre-filled so they
	// land on the generation flow with what the strategy learned already populated.
	const handleRespondProposal = useCallback(
		async (proposalId: string, decision: 'approve' | 'reject') => {
			if (decision === 'approve' && proposals) {
				const p = proposals.find((pr) => pr.id === proposalId);
				if (
					p?.change_type === 'recommend_next_step' &&
					p.recommended_step?.applier === 'propose_new_strategy' &&
					p.recommended_step.applier_params
				) {
					const params = p.recommended_step.applier_params;
					const seed: StrategySeed = {
						recommended_focus: String(params.recommended_focus || ''),
						reasoning: String(params.reasoning || ''),
						metrics_to_carry: Array.isArray(params.metrics_to_carry)
							? (params.metrics_to_carry as string[])
							: [],
						channel_suggestion: String(params.channel_suggestion || ''),
					};
					await onRespondProposal?.(proposalId, decision);
					setView({ kind: 'create', seed });
					return;
				}
			}
			await onRespondProposal?.(proposalId, decision);
		},
		[onRespondProposal, proposals],
	);

	// Outside-click closes: a pointerdown outside the menu frame and outside the resting dock
	// bar. The bar is excluded so its manage/orb controls own their own toggle (clicking manage
	// while open closes via its onPress, not a double-fire here).
	//
	// CRITICAL: the S2 dialogs this menu opens (the delete-confirm AlertDialog inside
	// StrategyDetail) render through React Aria's Overlay, which portals to document.body — a
	// SIBLING of this menu's own portal, never a descendant of frameRef. A naive "not inside
	// frameRef ⇒ outside" test therefore reads the user's first pointerdown inside such a
	// dialog (pressing Delete/Cancel) as an outside click, closes the menu, and unmounts the
	// dialog with it — making delete impossible. So only treat a click as outside when it
	// lands in the real app tree (#root) or
	// on the bare body itself. Anything in ANOTHER body-level portal is an overlay stacked above
	// this menu, and must not close it. This keys on portal STRUCTURE, not on RAC class names or
	// data attributes (which are styling details that change between versions).
	useEffect(() => {
		const onDown = (e: PointerEvent) => {
			const target = e.target as Element | null;
			if (!target) return;
			if (frameRef.current?.contains(target)) return;
			if (target.closest?.('[data-testid="strategy-dock-bar"]')) return;
			const inAppTree = Boolean(target.closest?.('#root'));
			const isBareBody = target === document.body;
			if (!inAppTree && !isBareBody) return;
			onClose();
		};
		// Capture so it runs before other handlers can stop propagation.
		document.addEventListener('pointerdown', onDown, true);
		return () => document.removeEventListener('pointerdown', onDown, true);
	}, [onClose]);

	return createPortal(
		<div
			ref={frameRef}
			data-testid="strategy-dock-frame"
			// The motion path actually taken — asserted deterministically by the e2e (morph in a
			// VT-capable browser, grow without VT, instant under reduced motion).
			data-dock-anim={animKind}
			style={{
				position: 'fixed',
				left: rect.left,
				top: rect.top,
				width: rect.width,
				// The rect height is a MAX (down to the column's bottom); the menu shrinks to
				// its content and only grows to this cap, at which point the card list scrolls.
				// Creating is the exception: the brief is a writing surface, so the menu takes the
				// full available height rather than hugging the editor's initial size.
				...(view.kind === 'create'
					? { height: rect.height }
					: { maxHeight: rect.height }),
				// The app-shell gray (#f8f8f8, RootLayout) — NOT white — so the menu reads as the
				// dock surface itself and the white glass cards inside pop as raised tiles. Set
				// --dock-fill HERE and paint from it: the bar rendered on top inherits this exact
				// value (the @property is `inherits: true`), so the dock and the menu are one
				// seamless color. Don't let either side override it while open, or a seam appears.
				['--dock-fill' as string]: DOCK_SURFACE_FILL,
				background: 'var(--dock-fill)',
				// Content clears the dock bar that overlays this surface's top edge — the bar
				// sits ON TOP of the menu so the two read as ONE connected element.
				paddingTop: rect.topInset,
				// Carries the shared morph name while open (the resting bar drops it), so the
				// View Transition tweens this box out of / back into the dock on open AND close.
				viewTransitionName: DOCK_FRAME_TRANSITION_NAME,
			}}
			className={cn(
				`relative z-50 flex flex-col overflow-hidden rounded-2xl border
				border-(--glass-border) shadow-[0_16px_48px_rgba(0,0,0,0.16)]`,
				// An S2 dialog opened from in here (delete-confirm) portals to document.body as
				// a SIBLING of this portal, and its overlay carries NO z-index — so at z-50 this
				// opaque surface paints straight over it and the dialog is invisible. React Aria
				// marks everything outside the modal `inert`, which is exactly the "a modal owns
				// the page" signal: drop out of the stacking order then, and the later-in-DOM
				// modal paints above. Keying on [inert] means this reverts the instant the dialog
				// closes, so the menu still sits above the app chrome the rest of the time.
				'inert:z-auto',
				// Only the no-VT fallback plays the CSS grow-in: the morph path is driven by the
				// View Transition (both at once would double-animate), and instant (reduced
				// motion) shows no animation at all.
				animKind === 'grow' && 'animate-dock-takeover-grow',
			)}
		>
			{/* The dock, painted on top of this surface's top edge. Same gray fill, so the bar
			    and the menu read as ONE element with the dock at its top. */}
			{bar && (
				<div
					data-testid="strategy-dock-menu-bar"
					className="pointer-events-none absolute top-0 left-1/2 z-10 flex
						-translate-x-1/2 items-start"
				>
					{bar}
				</div>
			)}
			<Dialog
				aria-label="Strategies"
				className="relative flex min-h-0 flex-1 flex-col pt-3 outline-none"
			>
				{view.kind === 'create' ? (
					<StrategyCreateView
						businessId={businessId}
						seed={view.seed}
						onBack={() => setView({ kind: 'list' })}
						onGenerate={onGenerate}
					/>
				) : view.kind === 'flags' ? (
					<FlagList
						strategyId={view.id}
						items={items}
						onAcknowledge={(flagId) => onAcknowledgeFlag(view.id, flagId)}
						onOpenPanel={(flagId) => onOpenFlagThread(view.id, flagId)}
						onOpenOwnerAsk={onOpenOwnerAsk}
						onOpenCampaignSetup={onOpenCampaignSetup}
						onBack={() => setView({ kind: 'list' })}
					/>
				) : view.kind === 'detail' ? (
					<div
						data-testid="strategy-detail-column"
						className="flex min-h-0 w-full flex-1 flex-col px-5"
					>
						{/* The detail owns its full header ([‹ Back] "Strategy" eyebrow row over
						    the title); Back returns to the list — no separate button row above
						    the title. */}
						<StrategyDetail
							id={view.id}
							onBack={() => setView({ kind: 'list' })}
							onDeleted={() => {
								onDeleted();
								setView({ kind: 'list' });
							}}
							onGenerateAgain={async () => {
								// Generate first, THEN land on the list: the new batch's rows are
								// already present, so the owner sees work rather than an empty list.
								await onGenerate();
								setView({ kind: 'list' });
							}}
							// A rejection deliberately stays on the failed screen, because that
							// screen is where the reason is shown. Only a started rebuild lands on
							// the list, where this variant now reads as building beside its
							// siblings.
							onRegenerate={async (strategyId) => {
								await onRegenerate(strategyId);
								setView({ kind: 'list' });
							}}
							onViewSetup={(pid) => {
								onViewSetup(pid);
								onClose();
							}}
						/>
					</div>
				) : (
					<ListView
						items={items}
						totalCount={totalCount}
						loading={loading}
						error={error}
						businessId={businessId}
						onOpenDetail={(id) => setView({ kind: 'detail', id })}
						// The card's route into the notification list. Only a running strategy
						// earns a bar orb, and the orb press was the list's only door, so a paused
						// strategy's open requests were unreachable on screen without this.
						onOpenFlags={(id) => setView({ kind: 'flags', id })}
						onOpenOwnerAsk={onOpenOwnerAsk}
						onCreate={() => setView({ kind: 'create' })}
						onActivate={onActivate}
						onReview={onReview}
						onPause={onPause}
						onRetry={onRetry}
						proposals={proposals}
						progressByStrategyId={progressByStrategyId}
						// Only when the caller actually gave us one. `CardProposals` uses an
						// absent handler to keep its buttons inert, and the wrapper is always
						// defined — passing it unconditionally would make approve look live
						// while nothing reached the server, and still navigate on a seed.
						onRespondProposal={
							onRespondProposal ? handleRespondProposal : undefined
						}
						onDiscussProposal={onDiscussProposal}
					/>
				)}
			</Dialog>
		</div>,
		document.body,
	);
}

// The menu header — a compact eyebrow-less title row (the menu is small; the big space-style
// eyebrow would waste its height). flex-shrink-0 so it never scrolls with the card list.
//
// It carries NO create control. Starting a strategy is the empty slot's job in the dock above:
// one affordance, in the collection it adds to. A second button here said the same thing twice,
// in the surface the slot is already sitting on. Filtering keys on the TOTAL, not on what is
// showing, so narrowing to nothing never removes the control that would widen it again.
function ListHeader({
	totalCount,
	filters,
	onAddFilter,
	onRemoveFilter,
}: {
	totalCount: number;
	filters: StrategyFilter[];
	onAddFilter: (f: StrategyFilter) => void;
	onRemoveFilter: (f: StrategyFilter) => void;
}) {
	return (
		<div
			data-testid="strategy-list-header"
			className="flex flex-shrink-0 items-center justify-between gap-4 pb-4"
		>
			<Heading
				level={1}
				styles={style({
					font: 'heading',
					fontWeight: 'bold',
					fontSize: 'heading',
				})}
			>
				Strategies
			</Heading>
			{totalCount > 0 && (
				<FilterToolbar
					filters={filters}
					dimensions={STRATEGY_FILTER_DIMENSIONS}
					onAdd={onAddFilter}
					onRemove={onRemoveFilter}
				/>
			)}
		</div>
	);
}

// A group heading inside the scroll list. Single-line and quiet — in a surface this small the
// heading has to cost almost nothing, and the word itself already carries the status, so there
// is no badge beside it.
function GroupHeading({ label, count }: { label: string; count: number }) {
	return (
		<div
			data-testid="strategy-group-heading"
			data-group-label={label}
			className="flex items-baseline gap-1.5 px-0.5 pt-1 text-xs font-semibold
				tracking-wide text-(--text-muted) uppercase first:pt-0"
		>
			<span>{label}</span>
			<span className="font-normal normal-case">({count})</span>
		</div>
	);
}

// The list screen: a fixed header (title + filters) over a body where only the card list
// scrolls. Cards stack ONE PER ROW (a vertical menu list), full width, grouped by lifecycle.
function ListView({
	items,
	totalCount,
	loading,
	error,
	businessId,
	onOpenDetail,
	onOpenFlags,
	onOpenOwnerAsk,
	onCreate,
	onActivate,
	onReview,
	onPause,
	onRetry,
	proposals,
	progressByStrategyId,
	onRespondProposal,
	onDiscussProposal,
}: {
	items: StrategyListItem[];
	totalCount: number;
	loading: boolean;
	error: string | null;
	businessId: string;
	onOpenDetail: (id: string) => void;
	// Required, not optional: this view has exactly one call site, so an optional callback would
	// hide a dropped wire behind a silent no-op instead of failing the build.
	onOpenFlags: (id: string) => void;
	onOpenOwnerAsk?: (actionId: string, message: string) => void;
	onCreate: () => void;
	onActivate: (id: string) => Promise<void>;
	onReview: (id: string, title?: string) => void;
	onPause: (id: string) => Promise<void>;
	onRetry: () => void | Promise<void>;
	proposals?: PendingProposal[];
	progressByStrategyId?: Record<string, { current_value: number }>;
	onRespondProposal?: (
		id: string,
		decision: 'approve' | 'reject',
	) => Promise<void>;
	onDiscussProposal?: (p: PendingProposal, strategyTitle?: string) => void;
}) {
	const column = 'flex min-h-0 w-full flex-1 flex-col px-5';
	const [filters, setFilters] = useState<StrategyFilter[]>([]);
	const addFilter = (f: StrategyFilter) =>
		setFilters((prev) =>
			prev.some((p) => p.dimension === f.dimension && p.value === f.value)
				? prev
				: [...prev, f],
		);
	const removeFilter = (f: StrategyFilter) =>
		setFilters((prev) =>
			prev.filter((p) => !(p.dimension === f.dimension && p.value === f.value)),
		);
	const groups = groupedDockRows(applyStrategyFilters(items, filters));
	const shown = groups.reduce((n, g) => n + g.items.length, 0);
	const header = (
		<ListHeader
			totalCount={totalCount}
			filters={filters}
			onAddFilter={addFilter}
			onRemoveFilter={removeFilter}
		/>
	);

	if (loading && shown === 0) {
		return (
			<div className={column}>
				{header}
				<div className="flex min-h-0 flex-1 items-center justify-center py-12">
					<ProgressCircle isIndeterminate aria-label="Loading strategies" />
				</div>
			</div>
		);
	}
	if (error && shown === 0) {
		return (
			<div className={column}>
				{header}
				<div
					className="flex min-h-0 flex-1 flex-col items-center justify-center
						gap-3 py-12 text-center"
				>
					<p className="text-base font-semibold">{error}</p>
					<Button variant="secondary" size="S" onPress={onRetry}>
						<Text>Try again</Text>
					</Button>
				</div>
			</div>
		);
	}
	// Nothing at all yet — the invitation. Distinct from "you have strategies, none showing".
	if (totalCount === 0) {
		return (
			<div className={column}>
				<ListHeader
					totalCount={0}
					filters={[]}
					onAddFilter={addFilter}
					onRemoveFilter={removeFilter}
				/>
				<div
					className="flex min-h-0 flex-1 flex-col items-center justify-center
						gap-4 py-12 text-center"
				>
					<StrategyInviteOrb className="[--orb-size:80px]" />
					<div className="space-y-1">
						<p className="text-base font-semibold text-(--text-primary)">
							Set your first strategy
						</p>
						<p className="max-w-[42ch] text-sm text-(--text-muted)">
							A strategy is a grounded plan for reaching a goal — the audience
							to win, the campaigns that move the needle, and how you&apos;ll
							measure it.
						</p>
					</div>
					<Button variant="accent" onPress={onCreate}>
						<Add />
						<Text>Generate a strategy</Text>
					</Button>
				</div>
			</div>
		);
	}
	if (shown === 0) {
		return (
			<div className={column} data-testid="strategy-list-column">
				{header}
				<div
					data-testid="strategy-list-none-showing"
					className="flex min-h-0 flex-1 flex-col items-center justify-center
						gap-2 py-12 text-center"
				>
					<p className="text-base font-semibold text-(--text-primary)">
						Nothing to show right now
					</p>
					<p className="max-w-[42ch] text-sm text-(--text-muted)">
						Your running strategies and your latest ideas appear here.
					</p>
				</div>
			</div>
		);
	}
	// Always label the groups, even when there is only one. Suppressing the lone heading made
	// the structure invisible exactly when the list needed explaining most: an owner with three
	// un-activated ideas saw an unlabelled stack and no way to tell what state anything was in.

	return (
		<div className={column} data-testid="strategy-list-column">
			{header}
			<div
				data-testid="strategy-list-scroll"
				className="scrollbar-hidden min-h-0 flex-1 overflow-y-auto pb-5"
			>
				{/* ONE flat child list with headings interleaved as siblings of the cards: a
				    per-group wrapper would move every card in the React tree whenever a group
				    appears or disappears, remounting them and re-firing their full-run fetch. */}
				<div
					data-testid="strategy-card-grid"
					className="flex flex-col items-stretch gap-3"
				>
					{groups.flatMap((group) => {
						const rows = group.items.map((item) => (
							<StrategyCard
								key={item.id}
								item={item}
								identity={identityFor(item)}
								enabled={Boolean(businessId)}
								onOpenDetail={onOpenDetail}
								onOpenFlags={onOpenFlags}
								onOpenOwnerAsk={onOpenOwnerAsk}
								onReview={onReview}
								onActivate={onActivate}
								onPause={onPause}
								currentValue={progressByStrategyId?.[item.id]?.current_value}
								proposals={proposals?.filter((p) => p.strategy_id === item.id)}
								onRespondProposal={onRespondProposal}
								onDiscussProposal={onDiscussProposal}
							/>
						));
						return [
							<GroupHeading
								key={`heading:${group.key}`}
								label={group.label}
								count={group.items.length}
							/>,
							...rows,
						];
					})}
				</div>
			</div>
		</div>
	);
}
